
  import { v4 as uuidv4 } from 'uuid'
  import SignViaMobile from '@/components/da-signer/SignViaMobile.vue'
  import ESignature from '@/components/da-signer/SignaturePad.vue'
  import ErrorPopup from '@/components/da-signer/ErrorPopup.vue'
  import AreYouSurePopup from '@/components/da-signer/AreYouSurePopup.vue'

  export default {
    name: `AeqDASigner`,
    components: {
      ErrorPopup,
      AreYouSurePopup,
      ESignature,
      SignViaMobile
    },
    props: {
      label: {
        type: String,
        default: ``
      },
      daCode: {
        type: String,
        default: ``
      },
      screeningUuid: {
        type: String,
        default: ``
      },
      value: {
        type: Object,
        default: undefined
      },
      errorMessages: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        accept: false,
        checkBoxOne: false,
        checkBoxTwo: false,
        error: null,
        pdfUrl: null,
        step: 1,
        showModal: false,
        showAreYouSure: false,
        loading: false,
        filteredProps: {},
        signature: null,
        showPhoneQR: false,
        QRToken: null,
        legalGuardianQRToken: null,
        activeMobileSignatureTarget: `user`,
        name: null,
        signaturePullInterval: null,
        city: null,
        date: null,
        underEighteen: false,
        legalGuardianName: null,
        innerValue: this.initValue(this.value),
        buffer: null,
        errorPopup: false,
        signatureExamples: [
          {
            imageUrl: `/images/declaration_of_authorisation/good_signature.jpg`,
            isGood: true,
            criteria: {
              photo: true,
              white_background: true,
              signature_centered: true,
              distance: true
            }
          },
          {
            imageUrl: `/images/declaration_of_authorisation/bad_signature_1.jpg`,
            isGood: false,
            criteria: {
              photo: false,
              white_background: true,
              signature_centered: true,
              distance: true
            }
          },
          {
            imageUrl: `/images/declaration_of_authorisation/bad_signature_2.jpg`,
            isGood: false,
            criteria: {
              photo: true,
              white_background: false,
              signature_centered: true,
              distance: true
            }
          },
          {
            imageUrl: `/images/declaration_of_authorisation/bad_signature_3.jpg`,
            isGood: false,
            criteria: {
              photo: true,
              white_background: true,
              signature_centered: false,
              distance: true
            }
          },
          {
            imageUrl: `/images/declaration_of_authorisation/bad_signature_4.jpg`,
            isGood: false,
            criteria: {
              photo: true,
              white_background: true,
              signature_centered: true,
              distance: false
            }
          }
        ]
      }
    },
    computed: {
      isSmallScreen() {
        return this.$vuetify.breakpoint.xsOnly
      },
      da() {
        return this.$store.getters[`daSigner/declarations`] || []
      },
      isDisabled() {
        return this.filteredProps?.disabled || false
      },
      requirements() {
        return { extensions: this.$config.public.images_extensions }
      },
      traductedErrors() {
        return this.$helpers.wrapInArray(this.errorMessages).map((e) => this.$t(e))
      },
      hasErrors() {
        return this.errorMessages?.length > 0
      },
      token() {
        return this.$router.history.current.query.token || this.$store.state.external.token
      },
      isLegalGuardianNameRequired() {
        return this.underEighteen && !this.da?.legal_guardian_name
      },
      activeMobileToken() {
        return this.activeMobileSignatureTarget === `legal_guardian` ? this.legalGuardianQRToken : this.QRToken
      }
    },
    mounted() {
      const parentObject = { $props: {} }
      this.filteredProps = Object.assign(parentObject.$props, this.$attrs)
      this.filteredProps.label = this.label
      this.QRToken = uuidv4()
      this.legalGuardianQRToken = uuidv4()
    },
    methods: {
      transformSignature(lines, target = `user`) {
        const signatureRef = target === `legal_guardian` ? `legalGuardianSignaturePad` : `signaturePad`
        this.$refs[signatureRef]?.fromData(lines)
      },
      addSignaturePullListener() {
        this.signaturePullInterval = setInterval(async () => {
          if (!this.showPhoneQR) {
            return
          }
          const data = { token: this.activeMobileToken }
          const response = await this.$store.dispatch(`daSigner/getSignature`, { data })
          if (response && response.signature) {
            const dataPoints = response.signature.data
            this.transformSignature(dataPoints, this.activeMobileSignatureTarget)
            this.showPhoneQR = false
          }
        }, 3000)
      },
      removeSignaturePullListener() {
        clearInterval(this.signaturePullInterval)
      },
      clearValues() {
        this.signature = null
        this.name = null
        this.city = null
        this.date = new Date().toISOString().substr(0, 10)
        this.underEighteen = false
        this.legalGuardianName = null
        this.activeMobileSignatureTarget = `user`
        this.accept = false
        this.checkBoxOne = false
        this.checkBoxTwo = false
        this.pdfUrl = null
        this.step = 1
        this.error = null
        this.buffer = null
      },
      initValue(value) {
        if (value) {
          if (!value.new) {
            value.new = []
          }
          if (!value.saved) {
            value.saved = []
          }
        } else {
          value = { new: [], saved: [] }
        }
        return JSON.parse(JSON.stringify(value))
      },
      getDocumentList() {
        let newDocs = []
        if (this.innerValue.new.length !== 0) {
          newDocs = [
            {
              sourceIndex: 0,
              size: this.innerValue.new[0].size,
              fileName: this.innerValue.new[0].name,
              downloadLink: false,
              deleteLink: false,
              deletable: true,
              data: this.innerValue.new[0].content
            }
          ]
        }
        return newDocs.concat(
          this.innerValue.saved.map((f, i) => ({
            sourceIndex: i,
            ...f
          }))
        )
      },
      async openModal() {
        this.clearValues()
        this.showModal = true
        await this.$store.dispatch(`daSigner/getDeclarations`, {
          code: this.daCode,
          uuid: this.screeningUuid,
          lang: this.$i18n.locale
        })
        this.underEighteen = Boolean(this.da?.under_eighteen)
        this.legalGuardianName = this.da?.legal_guardian_name || null
        this.addSignaturePullListener()
        this.$refs.signaturePad?.resizeCanvas()
        if (this.underEighteen) {
          this.$nextTick(() => this.$refs.legalGuardianSignaturePad?.resizeCanvas())
        }
      },
      deleteAndOpenModal() {
        this.innerValue.saved.forEach((doc) => {
          this.deleteDocument(doc)
        })
        this.innerValue.new = []
        this.showAreYouSure = false
        this.openModal()
      },
      areYouSure() {
        if (this.getDocumentList().length > 0) {
          this.showAreYouSure = true
          return
        }
        this.openModal()
      },
      closeModal() {
        this.showModal = false
        this.clearValues()
        this.removeSignaturePullListener()
      },
      refreshComponent(doc = null, emitBlur = true, emitInput = true) {
        console.log(`refreshComponent`, doc, emitInput)
        if (doc) {
          this.innerValue = this.initValue(doc)
          if (emitBlur) {
            this.$emit(`blur`, doc)
          }
        }
        if (emitInput) {
          this.$emit(`input`, this.innerValue)
        }
        this.refreshCounter += 1
      },
      async deleteDocument(doc) {
        if (doc.deleteLink) {
          const deleteLinkWithoutApi = doc.deleteLink.replace(`/frontoffice/`, ``)
          await this.$api.documents
            .delete(deleteLinkWithoutApi)
            .then(() => {
              this.innerValue.saved.splice(doc.sourceIndex, 1)
              this.$flashMessage.success({
                title: `${this.$t(`messages.success`)}`,
                message: this.$t(`messages.document_delete`)
              })
            })
            .catch(() => {
              this.$flashMessage.error({
                title: `<${this.$options.name}> : ${this.$t(`messages.error`)}`,
                message: this.$t(`messages.cant_delete_document`)
              })
            })
            .finally(() => this.refreshComponent())
        } else {
          this.innerValue.new.splice(doc.sourceIndex, 1)
          this.refreshComponent()
        }
      },
      onCloseModal(isOpen) {
        if (!isOpen) {
          this.clearValues()
        }
      },
      onUndereighteenChanged(value) {
        if (!value) {
          this.$refs.legalGuardianSignaturePad?.clear()
          if (this.activeMobileSignatureTarget === `legal_guardian`) {
            this.activeMobileSignatureTarget = `user`
            this.showPhoneQR = false
          }
        } else {
          this.$nextTick(() => this.$refs.legalGuardianSignaturePad?.resizeCanvas())
        }
      },
      openMobileSignature(target = `user`) {
        this.activeMobileSignatureTarget = target
        this.showPhoneQR = true
      },
      buildSignatureFile(dataURL, name) {
        return {
          name,
          size: 0,
          content_type: `image/png`,
          content: dataURL
        }
      },
      async nextStep() {
        if (this.step === 1) {
          const valid = await this.$refs?.acceptObs?.validate()
          if (this.$refs.signaturePad.isEmpty()) {
            this.$flashMessage.error({
              title: `${this.$t(`messages.error`)}`,
              message: `${this.$t(`components.da_signer.signature_pad.empty_signature`)}`
            })
            return
          }

          if (this.$refs.signaturePad.isTooSmall()) {
            this.$flashMessage.error({
              title: `${this.$t(`messages.error`)}`,
              message: `${this.$t(`components.da_signer.signature_pad.too_small_signature`)}`
            })
            return
          }
          if (this.underEighteen) {
            if (!this.$refs.legalGuardianSignaturePad || this.$refs.legalGuardianSignaturePad.isEmpty()) {
              this.$flashMessage.error({
                title: `${this.$t(`messages.error`)}`,
                message: `${this.$t(`components.da_signer.errors.legal_guardian_signature_required`)}`
              })
              return
            }

            if (this.$refs.legalGuardianSignaturePad.isTooSmall()) {
              this.$flashMessage.error({
                title: `${this.$t(`messages.error`)}`,
                message: `${this.$t(`components.da_signer.signature_pad.too_small_signature`)}`
              })
              return
            }
          }

          if (valid) {
            this.generateDA()
            this.step++
          }
        }
      },
      prevStep() {
        if (this.step > 1) {
          this.step--
          this.$refs.signaturePad.resizeCanvas()
          this.$refs.legalGuardianSignaturePad?.resizeCanvas()
        }
      },
      submit() {
        this.innerValue.new = this.buffer
        this.$emit(`input`, this.innerValue)
        this.$emit(`change`, this.innerValue)
        this.showModal = false
        this.removeSignaturePullListener()
      },
      getDocName() {
        return this.$i18n.t(`components.da_signer.doc_name`) + `${this.name}.pdf`
      },
      serializeDoc(blobContent) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => {
            console.log(blobContent)
            console.log(reader)
            resolve({
              size: blobContent.size,
              name: this.getDocName(),
              deletable: false,
              content_type: blobContent.type,
              content: reader.result
            })
          }
          reader.readAsDataURL(blobContent)
        })
      },
      dataURLtoBlob(dataURL) {
        const byteString = atob(dataURL.split(`,`)[1])
        const mimeString = dataURL.split(`,`)[0].split(`:`)[1].split(`;`)[0]
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const uint8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i)
        }
        return new Blob([arrayBuffer], { type: mimeString })
      },
      async generateDA() {
        this.pdfUrl = null
        const signatureDataURL = this.$refs.signaturePad.generate()
        const data = {
          name: this.name,
          city: this.city,
          date: this.date,
          signature: { new: [this.buildSignatureFile(signatureDataURL, `signature.png`)], saved: [] },
          code: this.daCode,
          under_eighteen: this.underEighteen,
          declarations: []
        }
        if (this.underEighteen) {
          const legalGuardianName = this.legalGuardianName?.trim()
          if (legalGuardianName) {
            data.legal_guardian_name = legalGuardianName
          }
          const legalGuardianSignatureDataURL = this.$refs.legalGuardianSignaturePad.generate()
          data.legal_guardian_signature = {
            new: [this.buildSignatureFile(legalGuardianSignatureDataURL, `legal_guardian_signature.png`)],
            saved: []
          }
        }
        let response
        try {
          response = await this.$store.dispatch(`daSigner/generateDA`, { data, uuid: this.screeningUuid, lang: this.$i18n.locale })
        } catch (error) {
          this.error = error
          this.step--
          await this.$refs.daSignerErrors.setErrors(error.data)
          this.errorPopup = true
          return
        }
        const newBlob = new Blob([response], { type: `application/pdf` })
        this.pdfUrl = URL.createObjectURL(newBlob)
        this.buffer = [await this.serializeDoc(newBlob)]
        this.loading = false
      }
    }
  }
